import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Download, 
  RefreshCw, 
  FileText, 
  Clock,
  ChevronLeft,
  ChevronRight,
  Database,
  History,
  File,
  HardDrive,
  Trash2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const LeadsPage = () => {
  const { api, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('files'); // 'files' or 'history'

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/ingest/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [api]);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/ingest/files');
      setFiles(res.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchDownloadHistory = useCallback(async () => {
    if (!isAdmin()) return;
    
    try {
      const res = await api.get(`/ingest/download-history?page=${historyPage}&limit=20`);
      setDownloadHistory(res.data.history || []);
      setHistoryTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching download history:', error);
    }
  }, [api, isAdmin, historyPage]);

  useEffect(() => {
    fetchStats();
    fetchFiles();
  }, [fetchStats, fetchFiles]);

  useEffect(() => {
    if (isAdmin() && activeTab === 'history') {
      fetchDownloadHistory();
    }
  }, [fetchDownloadHistory, isAdmin, activeTab]);

  // Manager download - one-time only
  const handleManagerDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/ingest/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded: ${filename}`);
      
      // Refresh files to update download status
      fetchFiles();
      fetchStats();
    } catch (error) {
      console.error('Download error:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to download file';
      toast.error(errorDetail);
    }
  };

  // Admin download - unlimited
  const handleAdminDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/ingest/files/${fileId}/admin-download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded: ${filename}`);
      
      // Refresh files and history
      fetchFiles();
      fetchStats();
      if (activeTab === 'history') {
        fetchDownloadHistory();
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download file');
    }
  };

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/ingest/files/${fileId}`);
      toast.success(`Deleted: ${filename}`);
      
      // Refresh files and stats after delete
      fetchFiles();
      fetchStats();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Manager View - Simple file list with one-time download
  if (!isAdmin()) {
    return (
      <div className="space-y-6" data-testid="leads-page-manager">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lead Files</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Download lead files for your team (one-time download per file)</p>
          </div>
          <Button onClick={() => { fetchStats(); fetchFiles(); }} variant="outline" size="icon" className="h-9 w-9">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                  <File className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Files</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.total_files || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500/10 to-teal-500/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                  <HardDrive className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Size</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatBytes(stats?.total_size_bytes)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                  <Download className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Downloads</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.total_downloads || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Files List */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Available Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <File className="w-12 h-12 mb-2" />
                <p>No files available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {files.map((file) => (
                  <div 
                    key={file.id}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                        <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{file.filename}</p>
                        <p className="text-xs text-slate-500">
                          {formatBytes(file.size_bytes)} • {formatDate(file.received_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {file.downloaded ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-200 dark:bg-slate-700 rounded-full">
                          <CheckCircle className="w-4 h-4 text-slate-500" />
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                            Downloaded {formatDate(file.downloaded_at)}
                          </span>
                        </div>
                      ) : (
                        <Button 
                          onClick={() => handleManagerDownload(file.id, file.filename)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin View - Full access with files list and download history
  return (
    <div className="space-y-6" data-testid="leads-page-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lead Files Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">View, download, and track ingested files</p>
        </div>
        <Button onClick={() => { fetchStats(); fetchFiles(); }} variant="outline" size="icon" className="h-9 w-9">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <File className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total Files</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.total_files || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500/10 to-teal-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <HardDrive className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total Size</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{formatBytes(stats?.total_size_bytes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Download className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Downloads</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.total_downloads || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-pink-500/10 to-rose-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-pink-600 dark:text-pink-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Recent Files</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.recent_files?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('files')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'files' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          Files ({stats?.total_files || 0})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <History className="w-4 h-4 inline mr-2" />
          Download History
        </button>
      </div>

      {/* Files Tab */}
      {activeTab === 'files' && (
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Ingested Files
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <File className="w-12 h-12 mb-2" />
                <p>No files ingested yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Filename</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Received</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Download Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {files.map((file) => (
                      <tr key={file.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-500" />
                            <span className="font-medium text-slate-900 dark:text-white">{file.filename}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {formatBytes(file.size_bytes)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(file.received_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            {file.downloaded ? (
                              <div className="flex items-center gap-1.5">
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                                  Manager downloaded {formatDate(file.downloaded_at)}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                <span className="text-xs text-amber-600 dark:text-amber-400">
                                  Not yet downloaded
                                </span>
                              </div>
                            )}
                            {file.admin_redownload_count > 0 && (
                              <span className="text-xs text-slate-500">
                                Admin re-downloaded ×{file.admin_redownload_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              onClick={() => handleAdminDownload(file.id, file.filename)}
                              size="sm"
                              variant="outline"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                            <Button 
                              onClick={() => handleDelete(file.id, file.filename)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Download History Tab */}
      {activeTab === 'history' && (
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Download History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {downloadHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History className="w-12 h-12 mb-2" />
                <p>No download history yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">User</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">File</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">Downloaded At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {downloadHistory.map((record) => (
                        <tr key={record.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900 dark:text-white">{record.user_name}</div>
                            <div className="text-xs text-slate-500">{record.user_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded ${
                              record.user_role === 'admin' 
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            }`}>
                              {record.user_role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {record.filename}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                            {formatDate(record.downloaded_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {historyTotalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-sm text-slate-500">
                      Page {historyPage} of {historyTotalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                        disabled={historyPage === historyTotalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadsPage;
